import { useEffect, useState } from 'react';
import { ChevronDown, Loader2, Plus, Save, Trash2, X } from 'lucide-react';

import ActionButton from '../../../components/Buttons/ActionButton';
import FormModal from '../../../components/Modals/FormModal';
import InputField from '../../../components/Forms/Input/InputField';
import ToggleSwitch from '../../../components/Forms/Input/ToggleSwitch';

import useAlerts from '../../../hooks/useAlerts';
import useApiErrorHandler from '../../../hooks/useApiErrorHandler';
import RestaurantCatalogExtensionService from '../../../services/restaurant/RestaurantCatalogExtensionService';
import { RestaurantModifierGroupDTO } from '../../../types/restaurant/RestaurantCatalogExtensionTypes';

interface Props {
  open: boolean;
  menuItemId: number | null;
  menuItemName?: string;
  branchId: number;
  onClose: () => void;
}

const EMPTY_GROUP = {
  name: '',
  minSelections: 0,
  maxSelections: 1,
  required: false,
};

const MenuItemModifiersModal = ({
  open,
  menuItemId,
  menuItemName,
  branchId,
  onClose,
}: Props) => {
  const { show } = useAlerts();
  const { handle } = useApiErrorHandler();

  const [groups, setGroups] = useState<RestaurantModifierGroupDTO[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [expanded, setExpanded] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [groupDraft, setGroupDraft] = useState(EMPTY_GROUP);
  const [showGroupDraft, setShowGroupDraft] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);

  // Borrador de opción por grupo: { [groupId]: { name, price } }
  const [optionDrafts, setOptionDrafts] = useState<
    Record<number, { name: string; price: string }>
  >({});
  const [creatingOptionFor, setCreatingOptionFor] = useState<number | null>(null);
  const [busyGroupId, setBusyGroupId] = useState<number | null>(null);
  const [busyOptionId, setBusyOptionId] = useState<number | null>(null);
  const [confirmDeleteGroupId, setConfirmDeleteGroupId] = useState<number | null>(
    null
  );

  useEffect(() => {
    if (!open || !menuItemId || !branchId) return;

    let cancelled = false;
    setExpanded(null);
    setShowGroupDraft(false);
    setGroupDraft(EMPTY_GROUP);
    setOptionDrafts({});
    setConfirmDeleteGroupId(null);
    setLoading(true);

    Promise.all([
      RestaurantCatalogExtensionService.menuItemModifierGroups(menuItemId),
      RestaurantCatalogExtensionService.listModifierGroups(branchId, {
        page: 0,
        size: 200,
        active: true,
      }),
    ])
      .then(([assigned, all]) => {
        if (cancelled) return;
        setGroups(all.content);
        setSelected(new Set(assigned.data.map((group) => group.id)));
      })
      .catch((error) => {
        if (!cancelled) handle(error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, menuItemId, branchId]);

  const toggleSelected = (id: number) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const createGroup = async () => {
    const name = groupDraft.name.trim();
    if (!name) {
      show('error', 'Falta el nombre', 'Escribe un nombre para el grupo.');
      return;
    }
    const min = Math.max(0, Number(groupDraft.minSelections) || 0);
    const max = Math.max(1, Number(groupDraft.maxSelections) || 1);
    if (min > max) {
      show('error', 'Rango inválido', 'El mínimo no puede superar al máximo.');
      return;
    }
    setCreatingGroup(true);
    try {
      const response = await RestaurantCatalogExtensionService.createModifierGroup(
        branchId,
        {
          name,
          description: null,
          minSelections: min,
          maxSelections: max,
          required: groupDraft.required,
          displayOrder: groups.length + 1,
          active: true,
        }
      );
      setGroups((current) => [response.data, ...current]);
      setSelected((current) => new Set(current).add(response.data.id));
      setExpanded(response.data.id);
      setShowGroupDraft(false);
      setGroupDraft(EMPTY_GROUP);
    } catch (error) {
      handle(error);
    } finally {
      setCreatingGroup(false);
    }
  };

  const createOption = async (group: RestaurantModifierGroupDTO) => {
    const draft = optionDrafts[group.id] ?? { name: '', price: '' };
    const name = draft.name.trim();
    if (!name) {
      show('error', 'Falta el nombre', 'Escribe un nombre para la opción.');
      return;
    }
    setCreatingOptionFor(group.id);
    try {
      const response = await RestaurantCatalogExtensionService.createModifier(
        branchId,
        {
          groupId: group.id,
          name,
          priceAdjustment: Number(draft.price) || 0,
          rawMaterialId: null,
          consumptionQuantity: null,
          consumptionUnitId: null,
          displayOrder: group.modifiers.length + 1,
          active: true,
        }
      );
      setGroups((current) =>
        current.map((item) =>
          item.id === group.id
            ? { ...item, modifiers: [...item.modifiers, response.data] }
            : item
        )
      );
      setOptionDrafts((current) => ({
        ...current,
        [group.id]: { name: '', price: '' },
      }));
    } catch (error) {
      handle(error);
    } finally {
      setCreatingOptionFor(null);
    }
  };

  const toggleRequired = async (group: RestaurantModifierGroupDTO) => {
    const nextRequired = !group.required;
    // Obligatorio exige al menos 1 selección mínima.
    const nextMin =
      nextRequired && group.minSelections < 1 ? 1 : group.minSelections;
    setBusyGroupId(group.id);
    try {
      const response =
        await RestaurantCatalogExtensionService.updateModifierGroup(
          branchId,
          group.id,
          {
            name: group.name,
            description: group.description,
            minSelections: nextMin,
            maxSelections: group.maxSelections,
            required: nextRequired,
            displayOrder: group.displayOrder,
            active: group.active,
          }
        );
      setGroups((current) =>
        current.map((item) =>
          item.id === group.id
            ? { ...response.data, modifiers: item.modifiers }
            : item
        )
      );
    } catch (error) {
      handle(error);
    } finally {
      setBusyGroupId(null);
    }
  };

  const removeGroup = async (group: RestaurantModifierGroupDTO) => {
    setBusyGroupId(group.id);
    setConfirmDeleteGroupId(null);
    try {
      await RestaurantCatalogExtensionService.deleteModifierGroup(
        branchId,
        group.id
      );
      setGroups((current) => current.filter((item) => item.id !== group.id));
      setSelected((current) => {
        const next = new Set(current);
        next.delete(group.id);
        return next;
      });
      show('success', 'Grupo eliminado', `Se eliminó "${group.name}".`);
    } catch (error) {
      handle(error);
    } finally {
      setBusyGroupId(null);
    }
  };

  const removeOption = async (
    group: RestaurantModifierGroupDTO,
    modifierId: number
  ) => {
    setBusyOptionId(modifierId);
    try {
      await RestaurantCatalogExtensionService.deleteModifier(
        branchId,
        modifierId
      );
      setGroups((current) =>
        current.map((item) =>
          item.id === group.id
            ? {
                ...item,
                modifiers: item.modifiers.filter((m) => m.id !== modifierId),
              }
            : item
        )
      );
    } catch (error) {
      handle(error);
    } finally {
      setBusyOptionId(null);
    }
  };

  const save = async () => {
    if (!menuItemId) return;
    setSaving(true);
    try {
      await RestaurantCatalogExtensionService.assignMenuItemModifierGroups(
        menuItemId,
        Array.from(selected)
      );
      show(
        'success',
        'Modificadores actualizados',
        'Los grupos de modificadores del ítem se guardaron correctamente.'
      );
      onClose();
    } catch (error) {
      handle(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal
      isOpen={open}
      onClose={saving ? undefined : onClose}
      title={
        menuItemName ? `Modificadores · ${menuItemName}` : 'Modificadores del ítem'
      }
      maxWidth="max-w-2xl"
      zIndex="top"
    >
      <div className="space-y-4">
        <p className="rounded-xl border border-primary/15 bg-primary/5 p-3 text-sm text-black dark:text-white">
          Marca los grupos que aplican a este ítem, o crea uno nuevo. Los grupos
          marcados se asignan al guardar.
        </p>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-7 w-7 animate-spin text-primary" />
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {groups.length === 0 && (
                <p className="rounded-lg border border-dashed border-stroke px-4 py-6 text-center text-sm text-bodydark2 dark:border-strokedark">
                  Aún no hay grupos de modificadores. Crea el primero abajo.
                </p>
              )}

              {groups.map((group) => {
                const isOpen = expanded === group.id;
                const draft = optionDrafts[group.id] ?? { name: '', price: '' };

                return (
                  <div
                    key={group.id}
                    className="rounded-xl border border-stroke dark:border-strokedark"
                  >
                    <div className="flex items-center gap-2 p-3">
                      <input
                        type="checkbox"
                        checked={selected.has(group.id)}
                        onChange={() => toggleSelected(group.id)}
                        className="h-4 w-4 shrink-0 accent-primary"
                        aria-label={`Asignar ${group.name}`}
                      />

                      <button
                        type="button"
                        onClick={() =>
                          setExpanded(isOpen ? null : group.id)
                        }
                        className="flex min-w-0 flex-1 items-center gap-2 text-left"
                      >
                        <span className="truncate font-medium text-black dark:text-white">
                          {group.name}
                        </span>
                        <span className="shrink-0 text-xs text-bodydark2">
                          {group.minSelections}–{group.maxSelections} ·{' '}
                          {group.modifiers.length}{' '}
                          {group.modifiers.length === 1 ? 'opción' : 'opciones'}
                        </span>
                        <ChevronDown
                          className={`h-4 w-4 shrink-0 text-bodydark2 transition ${
                            isOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </button>

                      <button
                        type="button"
                        disabled={busyGroupId === group.id}
                        onClick={() => toggleRequired(group)}
                        title="Alternar obligatorio / opcional"
                        className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold transition disabled:opacity-50 ${
                          group.required
                            ? 'bg-warning/15 text-warning hover:bg-warning/25'
                            : 'bg-gray-2 text-bodydark2 hover:bg-gray-3 dark:bg-meta-4'
                        }`}
                      >
                        {busyGroupId === group.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : group.required ? (
                          'Obligatorio'
                        ) : (
                          'Opcional'
                        )}
                      </button>

                      {busyGroupId === group.id ? (
                        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-danger" />
                      ) : confirmDeleteGroupId === group.id ? (
                        <button
                          type="button"
                          onClick={() => removeGroup(group)}
                          onBlur={() => setConfirmDeleteGroupId(null)}
                          className="shrink-0 rounded-lg bg-danger/10 px-2 py-1 text-[10px] font-semibold text-danger"
                        >
                          ¿Eliminar?
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteGroupId(group.id)}
                          title="Eliminar grupo (se quita de todos los platos)"
                          aria-label={`Eliminar ${group.name}`}
                          className="shrink-0 rounded-lg p-1.5 text-bodydark2 transition hover:bg-danger/10 hover:text-danger"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>

                    {isOpen && (
                      <div className="border-t border-stroke p-3 dark:border-strokedark">
                        {group.modifiers.length > 0 && (
                          <div className="mb-3 flex flex-wrap gap-1.5">
                            {group.modifiers.map((modifier) => (
                              <span
                                key={modifier.id}
                                className="inline-flex items-center gap-1 rounded-lg bg-gray-2 py-1 pl-2 pr-1 text-xs text-body dark:bg-meta-4 dark:text-bodydark"
                              >
                                {modifier.name}
                                {Number(modifier.priceAdjustment) !== 0 &&
                                  ` (${
                                    Number(modifier.priceAdjustment) > 0 ? '+' : ''
                                  }${modifier.priceAdjustment})`}
                                <button
                                  type="button"
                                  disabled={busyOptionId === modifier.id}
                                  onClick={() => removeOption(group, modifier.id)}
                                  title="Eliminar opción"
                                  aria-label={`Eliminar ${modifier.name}`}
                                  className="rounded p-0.5 text-bodydark2 transition hover:bg-danger/10 hover:text-danger disabled:opacity-50"
                                >
                                  {busyOptionId === modifier.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <X className="h-3 w-3" />
                                  )}
                                </button>
                              </span>
                            ))}
                          </div>
                        )}

                        <div className="flex flex-wrap items-end gap-2">
                          <input
                            value={draft.name}
                            placeholder="Nueva opción"
                            onChange={(event) =>
                              setOptionDrafts((current) => ({
                                ...current,
                                [group.id]: {
                                  ...draft,
                                  name: event.target.value,
                                },
                              }))
                            }
                            className="min-w-[140px] flex-1 rounded-lg border border-stroke bg-transparent px-3 py-1.5 text-sm outline-none focus:border-primary dark:border-strokedark dark:text-white"
                          />
                          <input
                            type="number"
                            step="0.01"
                            value={draft.price}
                            placeholder="Precio +/−"
                            onChange={(event) =>
                              setOptionDrafts((current) => ({
                                ...current,
                                [group.id]: {
                                  ...draft,
                                  price: event.target.value,
                                },
                              }))
                            }
                            className="w-28 rounded-lg border border-stroke bg-transparent px-3 py-1.5 text-sm outline-none focus:border-primary dark:border-strokedark dark:text-white"
                          />
                          <button
                            type="button"
                            disabled={creatingOptionFor === group.id}
                            onClick={() => createOption(group)}
                            className="inline-flex items-center gap-1 rounded-lg border border-primary/30 px-3 py-1.5 text-xs font-semibold text-primary transition hover:bg-primary/5 disabled:opacity-50"
                          >
                            {creatingOptionFor === group.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Plus className="h-3.5 w-3.5" />
                            )}
                            Opción
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {showGroupDraft ? (
              <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-black dark:text-white">
                    Nuevo grupo de modificadores
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowGroupDraft(false)}
                    className="text-bodydark2 hover:text-danger"
                    aria-label="Cerrar"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <InputField
                      label="Nombre del grupo"
                      name="groupName"
                      type="text"
                      value={groupDraft.name}
                      iconKey="tag"
                      placeholder="Ej. Punto de cocción"
                      onChange={(event) =>
                        setGroupDraft((current) => ({
                          ...current,
                          name: event.target.value,
                        }))
                      }
                    />
                  </div>
                  <InputField
                    label="Mín. selecciones"
                    name="groupMin"
                    type="number"
                    min={0}
                    value={groupDraft.minSelections}
                    onChange={(event) =>
                      setGroupDraft((current) => ({
                        ...current,
                        minSelections: Math.max(0, Number(event.target.value)),
                      }))
                    }
                  />
                  <InputField
                    label="Máx. selecciones"
                    name="groupMax"
                    type="number"
                    min={1}
                    value={groupDraft.maxSelections}
                    onChange={(event) =>
                      setGroupDraft((current) => ({
                        ...current,
                        maxSelections: Math.max(1, Number(event.target.value)),
                      }))
                    }
                  />
                  <div className="sm:col-span-2">
                    <ToggleSwitch
                      label="Obligatorio (el cliente debe elegir)"
                      name="groupRequired"
                      checked={groupDraft.required}
                      onChange={() =>
                        setGroupDraft((current) => ({
                          ...current,
                          required: !current.required,
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="mt-3 flex justify-end">
                  <ActionButton
                    label={creatingGroup ? 'Creando…' : 'Crear grupo'}
                    variant="primary"
                    size="sm"
                    fullWidth={false}
                    disabled={creatingGroup}
                    icon={
                      creatingGroup ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4" />
                      )
                    }
                    onClick={createGroup}
                  />
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowGroupDraft(true)}
                className="inline-flex items-center gap-2 rounded-xl border border-dashed border-primary/40 px-4 py-2.5 text-sm font-semibold text-primary transition hover:bg-primary/5"
              >
                <Plus className="h-4 w-4" />
                Nuevo grupo de modificadores
              </button>
            )}
          </>
        )}

        <div className="flex flex-col-reverse gap-3 border-t border-stroke pt-4 dark:border-strokedark sm:flex-row sm:justify-end">
          <ActionButton
            label="Cerrar"
            variant="secondary"
            size="md"
            fullWidth={false}
            disabled={saving}
            icon={<X className="h-4 w-4" />}
            onClick={onClose}
          />
          <ActionButton
            label={saving ? 'Guardando…' : 'Guardar asignación'}
            variant="primary"
            size="md"
            fullWidth={false}
            disabled={saving || loading}
            icon={
              saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )
            }
            onClick={save}
          />
        </div>
      </div>
    </FormModal>
  );
};

export default MenuItemModifiersModal;
